import { TournamentDetailsData, TournamentLeaderboardUserInfo } from 'config/types/tournament'
import { useCallback, useEffect, useMemo, useState } from 'react'
import TournamentService from 'services/TournamentService'
import { useAuth } from 'state/auth/hooks'

export const useFetchLeaderboard = (tournamentDetails: TournamentDetailsData, limitRow: number) => {
  return useCallback(
    async (offset: number, _limit: number) => {
      if (!tournamentDetails)
        return {
          items: [],
          paging: {},
        }

      const response = await TournamentService.getTournamentLeaderBoard(tournamentDetails?.id, {
        offset,
        limit: limitRow,
      }).call()

      return {
        items:
          response?.data?.items
            ?.map((item) => ({
              ...item,
              prize: tournamentDetails.rewards[item.rank],
            }))
            .filter((item) => Number(item.rank) < tournamentDetails.rewards.length) || [],
        paging: {
          ...response?.data?.paging,
          total:
            response?.data?.paging.total < tournamentDetails.rewards.length
              ? response?.data?.paging.total
              : tournamentDetails.rewards.length,
        },
      }
    },
    [tournamentDetails.id],
  )
}

export const useLeaderBoardUserInfo = (tournamentDetails: TournamentDetailsData, retrievalTime?: number) => {
  const [tournamentLeaderBoardUserInfo, setTournamentLeaderBoardUserInfo] = useState<TournamentLeaderboardUserInfo>()
  const { isSigned } = useAuth()

  useEffect(() => {
    if (!tournamentDetails?.id || !isSigned) {
      if (tournamentLeaderBoardUserInfo) setTournamentLeaderBoardUserInfo(null)
      return
    }

    const fetch = async () => {
      const response = await TournamentService.getTournamentLeaderboardUserInfo(tournamentDetails?.id).call()

      if (response?.data && response?.code === 'success') {
        const aboveUserInfo = {
          ...response.data.aboveUserInfo,
          prize: tournamentDetails.rewards[response.data.aboveUserInfo?.rank],
        }
        const userInfo = {
          ...response.data.userInfo,
          prize: tournamentDetails.rewards[response.data.userInfo?.rank],
        }
        setTournamentLeaderBoardUserInfo({
          aboveUserInfo,
          userInfo,
        })
      } else setTournamentLeaderBoardUserInfo(null)
    }

    fetch()
  }, [tournamentDetails?.id, retrievalTime, isSigned])

  return useMemo(
    () => ({
      aboveUserInfo: tournamentLeaderBoardUserInfo?.aboveUserInfo || null,
      userInfo: tournamentLeaderBoardUserInfo?.userInfo || null,
    }),
    [tournamentLeaderBoardUserInfo],
  )
}

export const useCompactTable = (tournamentDetails: TournamentDetailsData, retrievalTime?: number) => {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const { userInfo, aboveUserInfo } = useLeaderBoardUserInfo(tournamentDetails, retrievalTime)

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true)
      const response = await TournamentService.getTop3LeaderBoard(tournamentDetails?.id)

      if (response?.code === 'success') {
        setRows(
          response?.data?.items
            ?.map((item) => ({
              ...item,
              prize: tournamentDetails.rewards[item.rank],
            }))
            .filter((item) => Number(item.rank) < tournamentDetails.rewards.length) || [],
        )
      }
      setIsLoading(false)
    }
    fetch()
  }, [tournamentDetails?.id, retrievalTime])

  return useMemo(() => ({ rows, userInfo, aboveUserInfo, isLoading }), [rows, userInfo, aboveUserInfo, isLoading])
}

export const useActiveRankRange = (rows) => {
  const [activeRankRange, setActiveRangeRange] = useState<{ firstRank: number; lastRank: number }>()

  useEffect(() => {
    if (!rows.length) return
    const firstRank = Number(rows[0].rank)
    const lastRank = Number(rows[rows.length - 1].rank)
    setActiveRangeRange({ firstRank, lastRank })
  }, [rows])

  return useMemo(() => activeRankRange, [activeRankRange])
}
